import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  DEFAULT_SECURITY_SETTINGS,
  copyRowLimitFor,
  hasPermission,
  type Permission,
  type SecuritySettings,
  type SessionResponse,
  type SessionUser,
} from '@yana/shared';
import { api } from '@/api/client';
import { useUndoStack } from '@/composables/useUndoStack';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<SessionUser | null>(null);
  const loading = ref(false);
  /** Сессию ещё не проверяли — роутер должен подождать, а не редиректить */
  const checked = ref(false);
  let sessionGeneration = 0;

  /** Выход, истечение сессии и отзыв доступа уничтожают истории всех таблиц. */
  function clearSession(): void {
    sessionGeneration++;
    useUndoStack().clear();
    user.value = null;
    security.value = DEFAULT_SECURITY_SETTINGS;
    checked.value = true;
  }

  /**
   * Общие настройки безопасности — приезжают вместе с сессией.
   *
   * До ответа сервера действуют умолчания, а не «без ограничения»:
   * если бы предел появлялся только после загрузки, первое же нажатие
   * на медленной сети уносило бы в буфер всё выделенное.
   */
  const security = ref<SecuritySettings>(DEFAULT_SECURITY_SETTINGS);

  const isAuthenticated = computed(() => user.value !== null);
  const role = computed(() => user.value?.role ?? null);

  /**
   * Сколько строк можно скопировать за раз. `null` — сколько угодно.
   *
   * Решение принимает общая функция из @yana/shared: сервер отдаёт сырую
   * настройку, роль известна здесь, а два разных ответа на один вопрос
   * рано или поздно разойдутся.
   */
  const copyRowLimit = computed(() =>
    user.value ? copyRowLimitFor(security.value, user.value.role) : null,
  );

  /**
   * Проверка права на клиенте — только для того, чтобы не показывать
   * кнопку, которая всё равно вернёт 403. Источник истины — сервер.
   */
  function can(permission: Permission): boolean {
    if (!user.value) return false;
    return hasPermission(user.value.role, permission);
  }

  async function fetchMe(): Promise<void> {
    const generation = sessionGeneration;
    try {
      const me = await api.get<SessionResponse>('/session/me');
      // Ответ старого запроса не восстанавливает сессию после выхода.
      if (generation !== sessionGeneration) return;
      if (user.value?.id !== me.id || user.value.role !== me.role) useUndoStack().clear();
      user.value = me;
      security.value = me.security ?? DEFAULT_SECURITY_SETTINGS;
    } catch {
      if (generation === sessionGeneration) clearSession();
    } finally {
      checked.value = true;
    }
  }

  /** Администратор поменял настройки — прилетело по WebSocket. */
  function applySecurity(next: SecuritySettings): void {
    security.value = next;
  }

  async function login(login_: string, password: string): Promise<void> {
    clearSession();
    const generation = sessionGeneration;
    loading.value = true;
    try {
      await api.post('/session/login', { login: login_, password, rememberMe: true });
      if (generation !== sessionGeneration) return;
      await fetchMe();
    } finally {
      loading.value = false;
    }
  }

  async function logout(): Promise<void> {
    // Локальное завершение не ждёт сети и не оставляет окно для Ctrl+Z.
    clearSession();
    await api.post('/session/logout');
  }

  return {
    user,
    loading,
    checked,
    security,
    copyRowLimit,
    isAuthenticated,
    role,
    can,
    fetchMe,
    applySecurity,
    login,
    logout,
    clearSession,
  };
});
